import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  GitBranch,
  RefreshCcw,
  AlertCircle,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from '../../ui/icons';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import CopyButton from '../../ui/CopyButton';
import Colors from '../../../constants/Colors';
import {
  opsPurchaseTimelineApi,
  PurchaseJourney,
} from '../../../services/api';

function toApiDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function apiToDisplay(value: string): string {
  const [y, m, d] = value.split('-');
  if (!y || !m || !d) return value;
  return `${d}/${m}/${y}`;
}

function parseApiDate(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function todayApiDate(): string {
  return toApiDate(new Date());
}

type ThemedDatePickerProps = {
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (value: string) => void;
};

function ThemedDatePicker({ label, value, min, max, onChange }: ThemedDatePickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = parseApiDate(value);
  const minDate = min ? parseApiDate(min) : null;
  const maxDate = max ? parseApiDate(max) : null;
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => selectedDate ?? new Date());

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate) setViewMonth(selectedDate);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const monthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  const calendarStart = new Date(monthStart);
  calendarStart.setDate(monthStart.getDate() - monthStart.getDay());

  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(calendarStart);
    day.setDate(calendarStart.getDate() + index);
    return day;
  });

  const monthLabel = monthStart.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="analytics-filter analytics-date-filter" ref={containerRef}>
      <span className="analytics-filter-label">{label}</span>
      <button
        type="button"
        className="analytics-date-trigger"
        onClick={() => setIsOpen((open) => !open)}
        aria-label={`Select ${label.toLowerCase()} date`}
      >
        <span>{value ? apiToDisplay(value) : 'DD/MM/YYYY'}</span>
        <Calendar size={14} />
      </button>

      {isOpen && (
        <div className="analytics-date-popover">
          <div className="analytics-date-header">
            <button
              type="button"
              className="analytics-date-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <div className="analytics-date-title">{monthLabel}</div>
            <button
              type="button"
              className="analytics-date-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="analytics-date-grid analytics-date-grid-head">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((weekday, i) => (
              <span key={`${weekday}-${i}`}>{weekday}</span>
            ))}
          </div>

          <div className="analytics-date-grid">
            {days.map((day) => {
              const inMonth = day >= monthStart && day <= monthEnd;
              const isSelected =
                !!selectedDate &&
                day.getFullYear() === selectedDate.getFullYear() &&
                day.getMonth() === selectedDate.getMonth() &&
                day.getDate() === selectedDate.getDate();
              const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
              const isDisabled =
                (!!minDate && dayOnly < minDate) || (!!maxDate && dayOnly > maxDate);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={`analytics-date-day ${inMonth ? '' : 'muted'} ${isSelected ? 'selected' : ''}`}
                  disabled={isDisabled}
                  onClick={() => {
                    onChange(toApiDate(day));
                    setIsOpen(false);
                  }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const CHIP_META = {
  intent: { label: 'Intent', color: Colors.textLight },
  transaction: { label: 'Transaction', color: '#5B8DEF' },
  purchased: { label: 'Purchased', color: Colors.success },
  subscribed: { label: 'Subscribed', color: Colors.success },
  cancelled: { label: 'Cancelled', color: '#E67E22' },
  expired: { label: 'Expired', color: Colors.error },
} as const;

type ChipKind = keyof typeof CHIP_META;

function isSingleProduct(productId: string, source: Record<string, unknown>): boolean {
  const hay = [
    productId,
    source.product_id,
    source.product,
  ]
    .map((v) => String(v || '').toLowerCase())
    .join(' ');
  return hay.includes('single_game') || hay.includes('singlegame');
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === 1 || value === '1';
}

function epochToIso(value: unknown): string {
  const n = Number(value);
  if (!n) return '';
  const ms = n > 1e12 ? n : n * 1000;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function lifecycleEvents(
  journey: PurchaseJourney,
  purchaseEvents: PurchaseJourney['events'],
): PurchaseJourney['events'] {
  if (purchaseEvents.length === 0) return [];
  const source = (purchaseEvents[0].source || {}) as Record<string, unknown>;
  const purchasedAt = purchaseEvents[0].at || '';
  const status = String(source.subscription_status || '').toUpperCase();
  const cancelled = isTruthyFlag(source.cancel_at_period_end) || status === 'CANCELLED';
  const expired = status === 'EXPIRED';

  if (isSingleProduct(journey.product_id, source)) {
    return [{
      kind: 'purchased',
      at: purchasedAt,
      label: 'Purchased',
      detail: String(source.subscription_status || source.purchase_mode || ''),
      source,
    }];
  }

  const events: PurchaseJourney['events'] = [{
    kind: 'subscribed',
    at: purchasedAt,
    label: 'Subscribed',
    detail: String(source.subscription_status || source.purchase_mode || ''),
    source,
  }];
  if (cancelled || expired) {
    events.push({
      kind: 'cancelled',
      at: epochToIso(source.updated_at) || purchasedAt,
      label: 'Cancelled',
      detail: isTruthyFlag(source.cancel_at_period_end) ? 'cancel at period end' : status.toLowerCase(),
    });
  }
  if (expired) {
    events.push({
      kind: 'expired',
      at: epochToIso(source.expiry || source.subscription_expiry) || purchasedAt,
      label: 'Expired',
      detail: status.toLowerCase(),
    });
  }
  return events;
}

const FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'intent', label: 'Has Intent' },
  { value: 'transaction', label: 'Has Transaction' },
  { value: 'purchase', label: 'Has Product' },
  { value: 'full', label: 'All three present' },
];

function formatWhen(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB');
}

function PresenceChip({
  kind,
  count,
}: {
  kind: ChipKind;
  count?: number;
}) {
  const meta = CHIP_META[kind];
  return (
    <span
      style={{
        fontSize: '11px',
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: '999px',
        background: `${meta.color}22`,
        color: meta.color,
      }}
    >
      {meta.label}
      {count != null && count > 1 ? ` · ${count}` : ''}
    </span>
  );
}

function EventBlock({
  event,
  showConnector,
}: {
  event: PurchaseJourney['events'][number];
  showConnector: boolean;
}) {
  const color =
    CHIP_META[event.kind as ChipKind]?.color || CHIP_META.intent.color;

  return (
    <div style={{ display: 'flex', gap: '12px' }}>
      <div style={{ width: '14px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: color,
            marginTop: '4px',
            flexShrink: 0,
          }}
        />
        {showConnector && (
          <div style={{ flex: 1, width: '2px', background: Colors.divider, minHeight: '28px' }} />
        )}
      </div>
      <div style={{ flex: 1, paddingBottom: showConnector ? '14px' : '0', minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '13px' }}>{event.label}</div>
        <div style={{ fontSize: '11px', color: Colors.textLight, marginTop: '2px' }}>
          {formatWhen(event.at)}
          {event.detail ? ` · ${event.detail}` : ''}
        </div>
        {event.source && (
          <pre
            style={{
              marginTop: '8px',
              padding: '10px',
              borderRadius: '8px',
              background: Colors.background,
              fontSize: '11px',
              overflowX: 'hidden',
              overflowY: 'auto',
              maxHeight: '180px',
              fontFamily: "'SF Mono', monospace",
              color: Colors.text,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(event.source, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

function isLinkedIntent(
  event: PurchaseJourney['events'][number],
  journey: PurchaseJourney,
  purchaseEvents: PurchaseJourney['events'],
): boolean {
  const src = (event.source || {}) as Record<string, unknown>;
  const txnId = String(journey.transaction_id || '').trim();

  // Hard link: intent carries the same transaction_id
  if (txnId && String(src.transaction_id || '').trim() === txnId) return true;

  // Product record points at this intent
  const purchaseIntentIds = new Set(
    purchaseEvents
      .map((p) => String((p.source as Record<string, unknown> | undefined)?.intent_id || '').trim())
      .filter(Boolean),
  );
  if (src.intent_id && purchaseIntentIds.has(String(src.intent_id).trim())) return true;

  // Completed purchase intent
  const status = String(src.payment_status || '').toLowerCase();
  if (status === 'purchased' || status === 'completed') return true;

  return false;
}

function JourneyCard({ journey }: { journey: PurchaseJourney }) {
  const [open, setOpen] = useState(false);

  const intentEvents = journey.events.filter((e) => e.kind === 'intent');
  const txnEvents = journey.events.filter((e) => e.kind === 'transaction');
  const purchaseEvents = journey.events.filter((e) => e.kind === 'purchase');
  const productEvents = lifecycleEvents(journey, purchaseEvents);

  // Show every intent. Only the linked one(s) share the connector line with txn + product.
  let linkedIntents = intentEvents.filter((e) =>
    isLinkedIntent(e, journey, purchaseEvents),
  );
  let otherIntents = intentEvents.filter((e) => !linkedIntents.includes(e));

  // If we have txn/product but no intent matched, keep the newest intent on the line
  // so the purchase path still has an intent node — others stay unconnected.
  if (
    linkedIntents.length === 0 &&
    intentEvents.length > 0 &&
    (txnEvents.length > 0 || purchaseEvents.length > 0)
  ) {
    const sorted = [...intentEvents].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
    linkedIntents = [sorted[0]];
    otherIntents = intentEvents.filter((e) => e !== sorted[0]);
  }

  const connectedEvents = [...linkedIntents, ...txnEvents, ...productEvents];

  return (
    <div
      style={{
        border: `1px solid ${Colors.divider}`,
        borderRadius: '12px',
        background: Colors.cardBackground,
        overflow: 'hidden',
        minWidth: 0,
        // Compact grid tile when closed; an opened journey spans the whole row so its
        // timeline and JSON payloads get full width.
        gridColumn: open ? '1 / -1' : undefined,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '12px 14px',
          background: 'none',
          border: 0,
          cursor: 'pointer',
          color: Colors.text,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
              {intentEvents.length > 0 && (
                <PresenceChip kind="intent" count={intentEvents.length} />
              )}
              {txnEvents.length > 0 && <PresenceChip kind="transaction" />}
              {productEvents.map((event) => (
                <PresenceChip key={event.kind} kind={event.kind as ChipKind} />
              ))}
              {journey.match_type === 'soft' && (
                <span style={{ fontSize: '11px', color: Colors.textLight }}>soft match</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
              <div title={journey.email || journey.user_id || ''} style={{ minWidth: 0, fontWeight: 700, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {journey.email || journey.user_id || 'Unknown user'}
              </div>
              {(journey.email || journey.user_id) && (
                <CopyButton
                  text={journey.email || journey.user_id}
                  toastMessage={journey.email ? 'Email copied' : 'User ID copied'}
                  size={13}
                />
              )}
            </div>
            <div style={{ fontSize: '11px', color: Colors.textLight, marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span style={{ fontFamily: "'SF Mono', monospace" }}>{journey.product_id || '—'}</span>
              {journey.provider_or_store ? ` · ${journey.provider_or_store}` : ''}
              {journey.price_label ? ` · ${journey.price_label}` : ''}
            </div>
            {(journey.game_name || journey.game_id) && (
              <div title={journey.game_id || ''} style={{ fontSize: '12px', color: Colors.text, marginTop: '4px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {journey.game_name || 'Unknown game'}
                {journey.game_id && (
                  <span
                    style={{
                      marginLeft: '8px',
                      fontWeight: 400,
                      color: Colors.textLight,
                      fontFamily: "'SF Mono', monospace",
                      fontSize: '11px',
                    }}
                  >
                    {journey.game_id}
                  </span>
                )}
              </div>
            )}
            {journey.transaction_id && (
              <div
                style={{
                  fontSize: '11px',
                  color: Colors.textLight,
                  marginTop: '4px',
                  fontFamily: "'SF Mono', monospace",
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={journey.transaction_id}
              >
                txn {journey.transaction_id}
              </div>
            )}
          </div>
          <div style={{ fontSize: '10px', color: Colors.textLight, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {formatWhen(journey.latest_at)}
          </div>
        </div>
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${Colors.divider}` }}>
          <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Other intents: visible, no line into purchase path */}
            {otherIntents.map((event, idx) => (
              <EventBlock key={`other-intent-${idx}`} event={event} showConnector={false} />
            ))}

            {/* Linked intent(s) → transaction → product (connected) */}
            {connectedEvents.length > 0 && (
              <div>
                {connectedEvents.map((event, idx) => (
                  <EventBlock
                    key={`connected-${event.kind}-${idx}`}
                    event={event}
                    showConnector={idx < connectedEvents.length - 1}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PurchaseTimelinePanel() {
  const today = todayApiDate();
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [journeys, setJourneys] = useState<PurchaseJourney[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const listRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  const PAGE_SIZE = 20;

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      if (append) {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await opsPurchaseTimelineApi.list({
        from_date: fromDate,
        to_date: toDate,
        q: appliedSearch || undefined,
        presence: filter || undefined,
        offset,
        limit: PAGE_SIZE,
      });

      if (response.success && response.data?.success) {
        const page = response.data.journeys || [];
        setJourneys((prev) => (append ? [...prev, ...page] : page));
        setTotal(response.data.total ?? page.length);
        setHasMore(Boolean(response.data.has_more));
        setTotals(response.data.totals || {});
        setError(null);
      } else {
        if (!append) setJourneys([]);
        setHasMore(false);
        setError(response.error || response.data?.error || 'Failed to load purchase timeline');
      }

      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    },
    [fromDate, toDate, appliedSearch, filter],
  );

  useEffect(() => {
    fetchPage(0, false);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [fetchPage]);

  const onListScroll = () => {
    const el = listRef.current;
    if (!el || !hasMore || loadingMoreRef.current || loading) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom) {
      fetchPage(journeys.length, true);
    }
  };

  return (
    <div className="section">
      <h2 className="section-title">
        <GitBranch size={24} />
        Purchase Timeline
      </h2>

      <Card
        title="Intent → Transaction → Purchase"
        description="Journeys correlated across purchase-intents, transactions, and purchased-products"
        headerAction={
          <Button variant="outline" onClick={() => fetchPage(0, false)} loading={loading}>
            <RefreshCcw size={14} />
            Refresh
          </Button>
        }
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'flex-end',
            marginBottom: '16px',
          }}
        >
          <ThemedDatePicker
            label="From"
            value={fromDate}
            max={toDate}
            onChange={setFromDate}
          />
          <ThemedDatePicker
            label="To"
            value={toDate}
            min={fromDate}
            onChange={setToDate}
          />

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: Colors.textLight, marginBottom: '6px' }}>
              SHOW
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{
                padding: '7px 10px',
                borderRadius: '8px',
                border: `1px solid ${Colors.divider}`,
                background: Colors.cardBackground,
                color: Colors.text,
                fontSize: '13px',
              }}
            >
              {FILTER_OPTIONS.map((o) => (
                <option key={o.value || 'all'} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 220px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: Colors.textLight, marginBottom: '6px' }}>
              SEARCH
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setAppliedSearch(search.trim());
                }}
                placeholder="email, user_id, transaction_id, product_id"
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  borderRadius: '8px',
                  border: `1px solid ${Colors.divider}`,
                  background: Colors.cardBackground,
                  color: Colors.text,
                  fontSize: '13px',
                }}
              />
              <Button variant="outline" onClick={() => setAppliedSearch(search.trim())}>
                <Search size={14} />
              </Button>
            </div>
          </div>
        </div>

        <div style={{ fontSize: '12px', color: Colors.textLight, marginBottom: '12px' }}>
          Showing {journeys.length} of {total} · Intent: {totals.intent || 0} · Transaction: {totals.transaction || 0} · Product: {totals.purchase || 0}
        </div>

        {error && (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              color: Colors.error,
              marginBottom: '12px',
              fontSize: '13px',
            }}
          >
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <div
          ref={listRef}
          onScroll={onListScroll}
          style={{
            maxHeight: 'calc(100vh - 320px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '4px',
          }}
        >
          {loading && journeys.length === 0 ? (
            <div style={{ color: Colors.textLight, fontSize: '13px', padding: '20px 0' }}>Loading…</div>
          ) : journeys.length === 0 ? (
            <div style={{ color: Colors.inactive, fontSize: '13px', padding: '20px 0' }}>
              No journeys in this window.
            </div>
          ) : (
            <>
              {/* Compact journey cards, 2-3 per row on desktop (one column on narrow screens). */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))', gap: '10px', alignItems: 'start', marginBottom: '10px' }}>
                {journeys.map((j) => (
                  <JourneyCard key={j.journey_id} journey={j} />
                ))}
              </div>
              {loadingMore && (
                <div style={{ color: Colors.textLight, fontSize: '12px', padding: '10px 0', textAlign: 'center' }}>
                  Loading more…
                </div>
              )}
              {!hasMore && journeys.length > 0 && (
                <div style={{ color: Colors.inactive, fontSize: '12px', padding: '8px 0', textAlign: 'center' }}>
                  End of list
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
